import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Bug, Check, Copy, Download, FileText, Layers, Plus, Sparkles, Target } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PageHeading } from '../components/brain/BrainUI';
import ModelPicker from '../components/brain/ModelPicker';
import TicketDestination from '../components/tickets/TicketDestination';
import { TICKET_TEMPLATES, ticketBrief, ticketMarkdown, ticketQuestions } from '../lib/ticketTemplates';
import { loadTicketSession, newTicketSession, saveTicketSession } from '../services/ticketWriter';
import { writeTicket } from '../services/ollama';
import './TicketWriter.css';

const icons = { bug: Bug, epic: Layers, initiative: Target, prd: FileText };

export default function TicketWriter() {
  const { user } = useAuth();
  return <TicketWriterSession key={user?.id} userId={user?.id}/>;
}

function TicketWriterSession({ userId }) {
  const [session, setSession] = useState(() => loadTicketSession(userId));
  const [model, setModel] = useState('');
  const [busy, setBusy] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [storageError, setStorageError] = useState('');
  const [message, setMessage] = useState('');
  const [help, setHelp] = useState(null);
  const abort = useRef(null);
  const heading = useRef(null);
  const initialStep = useRef(true);
  const template = TICKET_TEMPLATES[session.type];
  const steps = ['Ticket type', ...template.steps.map(s => s.title), 'Review & send'];
  const current = template.steps[session.step - 1];
  const frozen = !!session.published || !!session.publishAttempt;
  const completed = ticketQuestions(session.type).filter(q => session.answers[q.id]?.trim()).length;

  useEffect(() => {
    try { saveTicketSession(userId, session); setStorageError(''); }
    catch { setStorageError('Your browser could not save this draft. Keep this page open and copy or download your work.'); }
  }, [session, userId]);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => {
    if (initialStep.current) { initialStep.current = false; return; }
    heading.current?.focus();
  }, [session.step]);

  const update = patch => setSession(s => ({ ...s, ...patch }));
  const answer = (id, value) => setSession(s => ({ ...s, answers: { ...s.answers, [id]: value }, draftStale: !!s.draft }));
  const navigate = step => { update({ step }); setError(''); setHelp(null); setMessage(''); };
  const chooseType = type => {
    if (type === session.type) return;
    if ((completed || session.draft) && !window.confirm('Changing the ticket type starts a new questionnaire. Replace the current draft?')) return;
    setSession(newTicketSession(type)); setHelp(null); setError('');
  };
  const startNew = () => {
    const prompt = session.publishAttempt
      ? 'The previous delivery is uncertain. Check Jira for that ticket before starting another one. Have you checked and want to replace this draft?'
      : 'Start a new ticket and replace this browser draft? Copy or download it first if you want to keep it.';
    if (!session.published && (completed || session.draft) && !window.confirm(prompt)) return;
    abort.current?.abort(); setSession(newTicketSession()); setHelp(null); setError(''); setMessage('');
  };
  const generate = async () => {
    const missingStep = template.steps.findIndex(step => step.questions.some(q => q.required && !session.answers[q.id]?.trim()));
    if (missingStep !== -1) { navigate(missingStep + 1); setError('Answer the required questions before generating your ticket.'); return; }
    await runAI();
  };
  const runAI = async fieldId => {
    if (!model || busy || frozen) return;
    const controller = new AbortController(); abort.current = controller;
    setBusy(fieldId || 'generate'); setError(''); setHelp(null); setMessage('');
    try {
      const result = await writeTicket({ model, brief: ticketBrief(session.type, session.answers), fieldId, signal: controller.signal });
      if (controller.signal.aborted) return;
      if (fieldId) setHelp({ fieldId, ...result.answer });
      else update({ draft: result.answer, draftStale: false, step: 4 });
    } catch (e) { if (!controller.signal.aborted) setError(e.message); }
    finally { if (!controller.signal.aborted) setBusy(''); }
  };
  const cancel = () => { abort.current?.abort(); setBusy(''); setMessage('AI request cancelled. Your answers are saved.'); };
  const next = event => { event.preventDefault(); session.step === 3 ? generate() : navigate(session.step + 1); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(ticketMarkdown(session.draft)); setMessage('Ticket copied.'); }
    catch { setError('Could not access the clipboard. Download the Markdown file or select and copy the draft text.'); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([ticketMarkdown(session.draft)], { type: 'text/markdown;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url;
    link.download = `${session.type}-${session.draft.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 70) || 'draft'}.md`;
    document.body.appendChild(link); link.click(); link.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const saveAttempt = attempt => {
    const updated = { ...session, publishAttempt: attempt };
    saveTicketSession(userId, updated);
    setSession(updated);
  };

  return <div className="v2-page ticket-writer">
    <PageHeading eyebrow="SPCC / TICKET WRITER" title="From rough idea to ready-to-review ticket" action={<button className="v2-button secondary" disabled={!!busy || publishing} onClick={startNew}><Plus size={16}/>New ticket</button>}>
      Work through a few focused questions. Let AI help with the words, then review and share your ticket.
    </PageHeading>
    <div className="ticket-layout">
      <aside className="ticket-progress" aria-label="Ticket progress">
        <div className="ticket-progress-heading"><FileText size={20}/><strong>{template.label} questionnaire</strong></div>
        <ol>{steps.map((label, index) => <li key={index}>
          <button type="button" aria-current={session.step === index ? 'step' : undefined} disabled={!!busy || publishing || (index > session.step && !(index === 4 && session.draft))} onClick={() => navigate(index)}>
            <span className="ticket-step-number">{index < session.step ? <Check size={14}/> : index + 1}</span><span>{label}</span>
          </button>
        </li>)}</ol>
        <p>{completed} of {ticketQuestions(session.type).length} questions answered</p>
        <p className="ticket-save-state" role="status">{storageError ? 'Draft is not saved' : 'Draft saved in this browser'}</p>
      </aside>
      <div className="ticket-workspace">
        <ModelPicker value={model} onChange={setModel} disabled={!!busy || frozen}/>
        {storageError && <p className="v2-error" role="alert">{storageError}</p>}
        {error && <p className="v2-error" role="alert">{error}</p>}
        {message && <p role="status" className="ticket-notice">{message}</p>}
        {session.step === 0 && <section className="v2-panel ticket-step">
          <p className="v2-label">Step 1 of 5</p><h2 tabIndex={-1} ref={heading}>What are you writing?</h2>
          <p>Each format has questions tailored to the work. You can answer in rough notes.</p>
          <div className="ticket-types" role="group" aria-label="Ticket type">{Object.entries(TICKET_TEMPLATES).map(([type, definition]) => {
            const Icon = icons[type];
            return <button type="button" className="ticket-type" key={type} aria-pressed={session.type === type} disabled={frozen} onClick={() => chooseType(type)}>
              <Icon size={22} aria-hidden="true"/><strong>{definition.label}</strong><span>{definition.description}</span>{session.type === type && <Check className="ticket-type-check" size={16} aria-hidden="true"/>}
            </button>;
          })}</div>
          <div className="v2-actions"><button className="v2-button" onClick={() => navigate(1)}>Start questionnaire<ArrowRight size={16}/></button></div>
        </section>}
        {current && <section className="v2-panel ticket-step">
          <p className="v2-label">Step {session.step + 1} of 5 · {template.label}</p><h2 tabIndex={-1} ref={heading}>{current.title}</h2>
          <p>Required questions are marked. Leave unknown details open for the team to resolve.</p>
          <form onSubmit={next}>
            <fieldset disabled={!!busy || frozen}>
              {current.questions.map(q => <div className="ticket-question" key={q.id}>
                <div className="v2-field"><label htmlFor={`ticket-${q.id}`}>{q.label}{q.required && ' (required)'}</label>
                  <small id={`hint-${q.id}`}>{q.hint}</small>
                  <textarea id={`ticket-${q.id}`} aria-describedby={`hint-${q.id}`} rows={3} required={q.required} maxLength={3000} value={session.answers[q.id] || ''} onChange={e => { answer(q.id, e.target.value); if (help?.fieldId === q.id) setHelp(null); }}/>
                </div>
                <button className="ticket-help-button" type="button" disabled={!model} aria-label={`Help me answer: ${q.label}`} onClick={() => runAI(q.id)}><Sparkles size={14}/>{busy === q.id ? 'Thinking…' : 'Help me answer'}</button>
                {help?.fieldId === q.id && <div className="ticket-ai-suggestion" role="region" aria-label={`AI help: ${q.label}`}>
                  <strong>Writing suggestion</strong><p>{help.guidance}</p>
                  {help.suggestion && <><blockquote>{help.suggestion}</blockquote><button type="button" className="v2-button secondary" onClick={() => { answer(q.id, help.suggestion); setHelp(null); }}>Use suggestion</button></>}
                  {!!help.follow_up_questions?.length && <><p>Details to consider</p><ul>{help.follow_up_questions.map((text, i) => <li key={i}>{text}</li>)}</ul></>}
                  <button className="ticket-help-button" type="button" onClick={() => setHelp(null)}>Dismiss</button>
                </div>}
              </div>)}
            </fieldset>
            {!model && <p className="ticket-notice">Choose an AI model above to get writing help and generate your ticket. You can fill in the questionnaire now.</p>}
            <div className="ticket-step-actions">
              <button className="v2-button secondary" type="button" disabled={!!busy} onClick={() => navigate(session.step - 1)}><ArrowLeft size={16}/>Back</button>
              {session.step === 3 ? <button className="v2-button" disabled={!!busy || !model || frozen}><Sparkles size={16}/>{busy === 'generate' ? 'Generating ticket…' : session.draft ? 'Regenerate draft from answers' : 'Generate ticket'}</button> : <button className="v2-button" disabled={!!busy}>Continue<ArrowRight size={16}/></button>}
            </div>
          </form>
          {busy && <div className="v2-actions" role="status"><span>{busy === 'generate' ? 'Turning your answers into a structured ticket…' : 'Preparing a suggestion…'}</span><button type="button" className="v2-button secondary" onClick={cancel}>Cancel</button></div>}
        </section>}
        {session.step === 4 && session.draft && <>
          <section className="v2-panel ticket-step">
            <p className="v2-label">Step 5 of 5 · {template.label}</p><h2 tabIndex={-1} ref={heading}>Review your ticket</h2>
            <p>Edit the title and Markdown description before sharing. Check proposed criteria and any missing details.</p>
            {session.draftStale && <p className="ticket-notice" role="status">Your answers changed since this draft was generated. Update the draft below, or go back and regenerate it.</p>}
            <fieldset disabled={frozen}>
              <label className="v2-field"><span>Ticket title</span><input required maxLength={255} value={session.draft.title} onChange={e => update({ draft: { ...session.draft, title: e.target.value } })}/></label>
              <label className="v2-field"><span>Ticket description (Markdown)</span><textarea className="ticket-draft-body" required maxLength={24000} rows={20} value={session.draft.description} onChange={e => update({ draft: { ...session.draft, description: e.target.value } })}/></label>
            </fieldset>
            {!!session.draft.open_questions?.length && <details className="ticket-open-questions"><summary>AI review notes · {session.draft.open_questions.length} open questions</summary><p>These notes reflect the generated draft. Resolve them in the description before sharing.</p><ul>{session.draft.open_questions.map((q, i) => <li key={i}>{q}</li>)}</ul></details>}
            <div className="v2-actions"><button className="v2-button secondary" onClick={copy}><Copy size={16}/>Copy ticket</button><button className="v2-button secondary" onClick={download}><Download size={16}/>Download Markdown</button><button className="v2-button secondary" disabled={frozen} onClick={() => navigate(3)}><ArrowLeft size={16}/>Edit answers</button></div>
          </section>
          <TicketDestination key={session.id} session={session} onAttempt={saveAttempt} onPublished={published => update({ published })} onBusy={setPublishing} disabled={!!busy || !!storageError}/>
        </>}
        <p className="ticket-footnote"><Sparkles size={14} aria-hidden="true"/>Your questionnaire answers are sent to the AI model you select. Suggestions only replace your answers when you choose to use them.</p>
      </div>
    </div>
  </div>;
}
